import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Star,
  MapPin,
  CheckCircle2,
  Sparkles,
  Globe,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import Services from "../components/homepage/Services";
import WhyLumotis from "../components/homepage/WhyLumotis";
import Process from "../components/homepage/Process";
import CaseStudies from "../components/homepage/CaseStudies";
import Testimonials from "../components/homepage/Testimonials";
import CTABanner from "../components/homepage/CTABanner";
import FAQSection from "../components/homepage/FAQSection";
import NotFoundPage from "./NotFoundPage";
import { MARKETS_CONFIG, MARKET_CODES } from "../data/markets";
import { US_TIER1_LOCATIONS } from "../data/usLocations";
import { TRUST_BADGES, PRICING } from "../data/site";

/* ─── Localized Hero ─────────────────────────────────────── */
function LocalizedHero({ m }) {
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 bg-white overflow-hidden"
      data-testid={`market-hero-${m.code}`}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
      <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
      <div
        aria-hidden
        className="absolute -left-40 bottom-0 w-[500px] h-[500px] glow-orb bg-electric/10 pointer-events-none"
      />

      <div className="container relative grid lg:grid-cols-12 gap-12 items-center">
        {/* Left */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="lg:col-span-7"
        >
          {/* Country pill */}
          <div className="inline-flex items-center gap-2.5 border border-electric/25 bg-electric/8 rounded-full px-4 py-1.5 mb-6">
            <span className="text-xl leading-none" aria-hidden>{m.flag}</span>
            <span className="text-overline text-electric">{m.trustBadge}</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[62px] leading-[1.04] font-extrabold text-charcoal text-balance">
            {m.h1Title}{" "}
            <span className="text-gradient">{m.h1Accent}</span>{" "}
            {m.h1Tail}
          </h1>

          <p className="mt-6 text-lg text-zinc-500 max-w-2xl leading-relaxed">
            {m.heroSubtitle}
          </p>

          <p className="mt-4 text-sm text-electric font-mono uppercase tracking-widest">
            {m.marketEmphasis}
          </p>

          {/* CTAs */}
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href="#audit"
              data-testid={`market-${m.code}-cta-audit`}
              className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30 rounded-xl"
            >
              Get Free Marketing Audit <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/case-studies"
              data-testid={`market-${m.code}-cta-cases`}
              className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-charcoal px-6 py-3.5 font-semibold hover:border-electric hover:text-electric transition-colors rounded-xl"
            >
              See {m.shortName} Results <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Stats row */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[m.localStat, TRUST_BADGES[1], TRUST_BADGES[2], TRUST_BADGES[3]].map(
              (b, i) => (
                <div
                  key={b.label}
                  className="rounded-2xl border border-zinc-100 bg-white px-4 py-4 shadow-card"
                >
                  <p className="font-display text-2xl md:text-3xl font-extrabold text-charcoal tracking-tight">
                    {b.value}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500 leading-snug">
                    {b.label}
                  </p>
                </div>
              )
            )}
          </div>
        </motion.div>

        {/* Right — visual card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.65 }}
          className="lg:col-span-5"
        >
          <div className="relative rounded-3xl bg-charcoal p-8 md:p-10 text-white overflow-hidden shadow-glow-brand">
            <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.08]" />
            <div
              aria-hidden
              className="absolute -right-12 -top-12 w-48 h-48 glow-orb bg-electric/25"
            />
            <div className="relative">
              <div className="flex items-center gap-3 mb-8">
                <Globe className="w-5 h-5 text-electric" />
                <span className="text-overline text-electric">{m.name} Market</span>
              </div>

              <p className="font-display text-5xl font-extrabold text-white mb-2">
                {m.localStat.value}
              </p>
              <p className="text-zinc-400 text-sm uppercase tracking-widest mb-8">
                {m.localStat.label}
              </p>

              <div className="space-y-3 mb-8">
                {m.localChannels.map((ch) => (
                  <div key={ch} className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-electric flex-shrink-0" />
                    <span className="text-sm text-zinc-300">{ch}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-0.5 mb-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-electric text-electric" />
                    ))}
                  </div>
                  <p className="text-xs text-zinc-400">
                    Rated 4.9/5 by {m.shortName} clients
                  </p>
                </div>
                <div className="w-10 h-10 grid place-items-center rounded-xl bg-electric/15 border border-electric/30">
                  <MapPin className="w-4 h-4 text-electric" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Local Market Insights ──────────────────────────────── */
function LocalInsights({ m }) {
  if (!m.localInsights || m.localInsights.length === 0) return null;
  return (
    <section className="py-20 md:py-28 bg-white border-b border-zinc-100">
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <p className="text-overline text-electric mb-3">
              {m.flag} {m.shortName} Market Intelligence
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
              Why digital marketing in {m.name} is different.
            </h2>
          </div>
          <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
            Three market-specific realities that shape every campaign we run for {m.shortName} clients.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {m.localInsights.map((ins, idx) => (
            <motion.div
              key={ins.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
              className="rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 flex flex-col shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all"
            >
              <p className="font-display text-5xl md:text-6xl font-extrabold text-charcoal leading-none mb-3">
                {ins.stat}
              </p>
              <p className="font-display font-bold text-charcoal text-base leading-snug mb-2">
                {ins.label}
              </p>
              <p className="text-sm text-zinc-500 leading-relaxed">{ins.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Local Channels ─────────────────────────────────────── */
function LocalChannels({ m }) {
  return (
    <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.06]" />
      <div
        aria-hidden
        className="absolute -right-24 top-0 w-[440px] h-[440px] glow-orb bg-electric/15 pointer-events-none"
      />
      <div className="container relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="text-overline text-electric mb-3">
              {m.shortName} Channel Mix
            </p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-balance leading-[1.05] mb-6">
              The channels we run for {m.shortName} businesses.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-lg">
              Every market has a different channel hierarchy. Here's what works in {m.name} — and how we coordinate them into one revenue engine.
            </p>
            <Link
              to="/free-aso-audit"
              className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 transition-all shadow-lg shadow-electric/30"
            >
              Get a free {m.shortName} audit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-6 flex flex-wrap gap-3">
            {m.localChannels.map((ch, idx) => (
              <motion.div
                key={ch}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: idx * 0.07 }}
                className="inline-flex items-center gap-2 border border-white/15 bg-white/5 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:border-electric hover:bg-electric/10 transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-electric flex-shrink-0" />
                {ch}
              </motion.div>
            ))}

            <div className="inline-flex items-center gap-2 text-sm text-zinc-400 w-full mt-2">
              <Sparkles className="w-4 h-4 text-electric" />
              <span>All channels coordinated under one strategy — compounding, not competing.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Localized Pricing ──────────────────────────────────── */
function LocalizedPricing({ m }) {
  const formatPrice = (p) => {
    if (p.price.startsWith("$")) {
      const n = parseInt(p.price.replace(/[^0-9]/g, ""), 10);
      const local = Math.round((n * m.multiplier) / 10) * 10;
      return `${m.currency}${local.toLocaleString()}`;
    }
    return p.price;
  };

  return (
    <section
      id="pricing"
      className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100"
      data-testid={`market-${m.code}-pricing`}
    >
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-overline text-electric mb-3">
            {m.shortName} Pricing
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
            Transparent pricing for {m.name} businesses.
          </h2>
          <p className="mt-5 text-lg text-zinc-500">
            Shown in {m.currency.trim()} — monthly, cancel anytime, no lock-ins.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {PRICING.map((p) => (
            <article
              key={p.tier}
              className={`relative p-8 md:p-10 flex flex-col rounded-2xl ${
                p.highlighted
                  ? "bg-charcoal text-white -translate-y-2 shadow-glow-brand"
                  : "bg-white border border-zinc-100 shadow-card"
              }`}
              data-testid={`market-${m.code}-pricing-${p.tier.toLowerCase()}`}
            >
              {p.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-electric text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <p
                className={`text-overline ${p.highlighted ? "text-electric" : "text-zinc-500"} mb-3`}
              >
                {p.tier}
              </p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-display text-5xl md:text-6xl font-black tracking-tight">
                  {formatPrice(p)}
                </span>
                {p.cadence && (
                  <span className={`text-sm ${p.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                    {p.cadence}
                  </span>
                )}
              </div>
              <p className={`text-sm leading-relaxed mb-8 ${p.highlighted ? "text-zinc-300" : "text-zinc-600"}`}>
                {p.description}
              </p>
              <ul className="space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <CheckCircle2
                      className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlighted ? "text-electric" : "text-electric"}`}
                      strokeWidth={2.5}
                    />
                    <span className={p.highlighted ? "text-zinc-200" : "text-zinc-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#audit"
                className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3.5 font-semibold rounded-xl transition-all ${
                  p.highlighted
                    ? "bg-electric text-white hover:bg-electric-500 shadow-lg shadow-electric/30"
                    : "bg-charcoal text-white hover:bg-electric"
                }`}
              >
                {p.cta} <ArrowRight className="w-4 h-4" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Localized Testimonial ──────────────────────────────── */
function LocalizedTestimonial({ m }) {
  return (
    <section
      className="py-20 md:py-24 bg-white border-t border-zinc-100"
      data-testid={`market-${m.code}-testimonial`}
    >
      <div className="container max-w-4xl">
        <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-10 md:p-14 shadow-card text-center">
          <p className="text-overline text-electric mb-6">
            {m.flag} {m.shortName} Client Voice
          </p>
          <div className="flex items-center justify-center gap-0.5 mb-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-electric text-electric" />
            ))}
          </div>
          <blockquote className="font-display text-2xl md:text-3xl text-charcoal leading-snug font-bold text-balance mb-8">
            "{m.testimonial.text}"
          </blockquote>
          <p className="text-zinc-500 text-sm font-medium">
            — {m.testimonial.name},{" "}
            <span className="text-zinc-400">{m.testimonial.role}</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── USA: links to Tier-1 city/state hubs (internal-link equity from /us) ── */
function UsLocationsBlock() {
  return (
    <section className="py-16 md:py-20 bg-white border-y border-zinc-100" data-testid="us-locations-block">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <p className="text-overline text-electric mb-3">🇺🇸 Local expertise</p>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-[1.05]">
              Digital marketing, localised to your city and state.
            </h2>
          </div>
          <Link
            to="/us/locations"
            className="inline-flex items-center gap-2 border border-zinc-200 text-charcoal px-5 py-2.5 text-sm font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors w-fit"
          >
            All US locations <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {US_TIER1_LOCATIONS.map((l) => (
            <Link
              key={l.slug}
              to={`/us/${l.slug}`}
              className="group rounded-xl border border-zinc-100 bg-white px-4 py-3.5 shadow-card hover:border-electric/30 hover:shadow-card-hover transition-all"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xl leading-none" aria-hidden>{l.emoji || "🏙️"}</span>
                <span className="text-[10px] uppercase tracking-widest text-zinc-400">{l.type}</span>
              </div>
              <p className="font-display font-bold text-charcoal group-hover:text-electric transition-colors leading-snug">{l.name}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Main Page ──────────────────────────────────────────── */
export default function MarketHomePage() {
  const { market } = useParams();
  const code = (market || "").toLowerCase();
  const m = MARKETS_CONFIG[code];

  // Unknown single-segment path (e.g. a typo'd URL) → show the 404 page
  // instead of silently redirecting home.
  if (!m) return <NotFoundPage />;

  const canonicalUrl = `https://lumotis.com/${m.code}`;

  return (
    <>
      <Helmet>
        <html lang={m.locale.split("_")[0]} />
        <title>{m.metaTitle}</title>
        <meta name="description" content={m.metaDescription} />
        <meta name="keywords" content={m.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={m.metaTitle} />
        <meta property="og:description" content={m.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={m.locale} />
        <meta property="og:type" content="website" />

        {/* hreflang for all markets */}
        <link rel="alternate" hrefLang="x-default" href="https://lumotis.com/" />
        {MARKET_CODES.map((c) => (
          <link
            key={c}
            rel="alternate"
            hrefLang={MARKETS_CONFIG[c].locale.replace("_", "-")}
            href={`https://lumotis.com/${c}`}
          />
        ))}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `Lumotis — ${m.name} Digital Marketing Agency`,
            description: m.metaDescription,
            url: canonicalUrl,
            areaServed: { "@type": "Country", name: m.name },
            parentOrganization: {
              "@type": "Organization",
              name: "Lumotis",
              url: "https://lumotis.com",
            },
          })}
        </script>
      </Helmet>

      <Navbar />
      <main data-testid={`market-home-${m.code}`}>
        <LocalizedHero m={m} />
        <LocalInsights m={m} />
        <Services />
        <LocalChannels m={m} />
        {m.code === "us" && <UsLocationsBlock />}
        <WhyLumotis />
        <Process />
        <CaseStudies />
        <LocalizedTestimonial m={m} />
        <Testimonials />
        <LocalizedPricing m={m} />
        <CTABanner />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}
